<template>
  <a-table :columns="columns" :data-source="invalidRes" :pagination="false">
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'themeId'">
        {{ getThemeName(record.themeId) }}
      </template>
      <template v-else-if="column.key === 'colorId'">
        {{ colorName(record.themeId, record.colorId ?? '') }}
      </template>
      <template v-else-if="column.key === 'shapeId'">
        <span class="whitespace-nowrap">
          <icon
            :width="16"
            :height="16"
            :icon="ICON_SRC[(Shape.getShape(record.shapeId) as Shape).type]"
          />
          {{ (Shape.getShape(record.shapeId) as Shape).attrs.name }}
        </span>
      </template>
      <template v-else-if="column.key === 'operation'">
        <a-button size="small" @click="() => handleLayerClick(record)"
          >查看</a-button
        >
      </template>
    </template>
  </a-table>
</template>

<script setup lang="ts">
import { ICON_SRC } from '@/hooks';
import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { WatchfaceInvalidItem } from '@/utils/watchface-export/validWatchfaceThemes';
import { PropType, ref } from 'vue';

defineProps({
  invalidRes: {
    type: Array as PropType<WatchfaceInvalidItem[]>,
    default: () => []
  }
});

const layerStore = useLayerStore();
const editorStore = useEditorStore();

const columns = [
  {
    title: '所属样式',
    dataIndex: 'themeId',
    key: 'themeId'
  },
  {
    title: '所属配色',
    dataIndex: 'colorId',
    key: 'colorId'
  },
  { title: '图层', dataIndex: 'shapeId', key: 'shapeId' },
  { title: '问题', dataIndex: 'errMsg', key: 'errMsg' },
  { title: '操作', key: 'operation' }
];

const visible = ref(false);

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const colorName = (themeId: string, colorId: string) => {
  const colorGroupTable = layerStore.themes.find(
    (i) => i.id === themeId
  )?.colorGroupTable;

  if (!colorGroupTable) return '-';
  // feat: 导出报错弹框优化，未传colorId（非单一配色问题）时展示所有配色，避免开发者理解错误
  if (!colorId) return colorGroupTable.map((i) => i.name).join('、');

  return (
    layerStore.themes
      .find((i) => i.id === themeId)
      ?.colorGroupTable.find((i) => i.id === colorId)?.name ?? '-'
  );
};

const handleLayerClick = (i: WatchfaceInvalidItem) => {
  visible.value = false;
  editorStore.selectTheme(i.themeId);
  i.colorId && editorStore.selectThemeColor(i.colorId);

  // 如果错误元素在容器的非当前显示组内，自动切换容器显示到该组
  const shape = Shape.getShape(i.shapeId);
  if (shape) {
    let parent = shape.parent;
    while (parent) {
      if (parent.parent?.type === ShapeType.Slot) {
        const slot = parent.parent;
        if (slot.attrs.widgetIdToDisplay !== parent.id) {
          slot.setAttrs({ widgetIdToDisplay: parent.id });
        }
        break;
      }
      parent = parent.parent;
    }
  }

  editorStore.select([i.shapeId]);
  emit('close');
};

const getThemeName = (themeId: string) => {
  return layerStore.themeLabelName(themeId);
};
</script>
<style lang="less"></style>
